import { useCallback, useEffect, useState } from 'react';
import type { ConVersion } from '../../../api/cliente';
import type { AjustesAgenda, Recolocacion } from '../../../api/ops/servicios';
import type { DiaEspecial, NuevaSemana, SemanaTipo } from '../../../api/ops/disponibilidad';
import { ErrorApi } from '../../../api/problema';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Boton } from '../../../componentes/Boton';
import { Chip } from '../../../componentes/Chip';
import { Contexto } from '../../../componentes/Contexto';
import { Deshacer } from '../../../componentes/Deshacer';
import { EstadoCargando, EstadoError } from '../../../componentes/EstadoPagina';
import { Fecha } from '../../../componentes/Fecha';
import { Icono } from '../../../componentes/Icono';
import { Interruptor } from '../../../componentes/Interruptor';
import { OpcionRadio } from '../../../componentes/OpcionRadio';
import { Ventana } from '../../../componentes/Ventana';
import { estadoDeError } from '../../../logica/estados';
import { anioDe, baldosa, bloquesDeSemana, erroresDeBloques, fechaLarga, hayErrores, hoyEn, semanaDeBloques, semanaDeTramos, sumarDias, tramosDeSemana, type Bloque } from '../../../logica/horario';
import { useCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { textoDeError } from '../../agenda/logica';
import { useCambios, type Cambio } from '../../horario/cambios';
import { Impacto } from '../../horario/Impacto';
import { Semana } from '../../horario/Semana';
import { palabrasDe, tramoPorDefecto } from '../../horario/textos';
import '../../horario/horario.css';
import { useDatosServicios } from '../datos';
import { primerNombre, unir } from '../logica';
import { AYUDA, PERMISOS, SOLO_LECTURA } from '../textos';
import { DiaEspecialPanel } from './DiaEspecialPanel';
import { Resolver } from './Resolver';
import { ESTADO_RECOLOCACION, POR_QUE, lineaDiaEspecial } from './logica';

interface Borrador {
    hereda: boolean;
    bloques: Bloque[];
}

interface DatosProfesional {
    vigente: SemanaTipo | null;
    semanaNegocio: SemanaTipo | null;
    dias: DiaEspecial[];
}

const PALABRAS = palabrasDe(undefined, true);

/**
 * 9.7 «Horarios» de los profesionales (`?apartado=horarios&profesional={id}`), con las mismas piezas que 3.1: el profesional
 * en RV Contexto, su semana tipo (la del negocio o una propia) por bloques con «Se aplica desde», sus días especiales
 * (vacaciones, médico, formación…) y, arriba, las citas afectadas por resolver (`listAppointmentRelocations`). Todo cambio
 * pasa por la vista previa (`horario/cambios.ts`): sin afectados, «Deshacer» 5 s; con afectados, la ventana 3.4. Al guardar
 * una semana que se sale del horario del negocio, aviso «se guarda igual» (`warnings.outside_business_hours`, G437, G460).
 * Abajo, si los profesionales pueden cambiar su horario desde Mi agenda y cómo (G86, G254).
 */
export function Horarios(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, ruta, busqueda, navegar, membresia } = props;
    const { puede } = usePermisos();
    const avisar = useAvisoBreve();
    const editable = puede(PERMISOS.gestionarHorarios) && !soloLectura;
    const puedeConfirmar = puede(PERMISOS.horarios);
    const resuelve = puede(PERMISOS.recolocar) && !soloLectura;
    const { datos, error, reintentar } = useDatosServicios(ops, negocio, puede);
    const zona = membresia?.timezone;
    const hoy = hoyEn(zona);
    const anio = anioDe(hoy);
    const c = useCambios({ ops, negocio, avisar, palabras: PALABRAS });

    const activos = (datos?.profesionales ?? []).filter((p) => p.status === 'active');
    const pedido = busqueda.get('profesional');
    const elegido = activos.find((p) => p.id === pedido) ?? activos[0] ?? null;
    const [deUno, setDeUno] = useState<{ id: string; datos: DatosProfesional } | null>(null);
    const [errorUno, setErrorUno] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [borradores, setBorradores] = useState<Readonly<Record<string, Borrador>>>({});
    const [desde, setDesde] = useState<Readonly<Record<string, string>>>({});
    const [relocs, setRelocs] = useState<Recolocacion[]>([]);
    const [nombres, setNombres] = useState<Readonly<Record<string, string>>>({});
    const [resolver, setResolver] = useState<Recolocacion | null>(null);
    const [dia, setDia] = useState<{ dia: DiaEspecial | null } | null>(null);
    const [copiar, setCopiar] = useState<{ a: string[]; desde: string } | null>(null);
    const [ajustes, setAjustes] = useState<ConVersion<AjustesAgenda> | null>(null);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    // Lo del profesional elegido: su semana tipo vigente, la del negocio (para empezar la propia y para verla) y sus días especiales.
    const calId = elegido?.calendar_id ?? null;
    useEffect(() => {
        if (calId === null || elegido === null) {
            return;
        }
        let vivo = true;
        const id = elegido.id;
        Promise.all([
            ops.listWeeklySchedules(negocio, calId, { limit: 50 }),
            ops
                .listCalendars(negocio, { kind: 'business', limit: 1 })
                .then((l) => (l.data[0] === undefined ? null : ops.listWeeklySchedules(negocio, l.data[0].id, { limit: 50 })))
                .catch(() => null),
            ops.listExceptions(negocio, calId, hoy, sumarDias(hoy, 91), { limit: 100 }),
        ])
            .then(([semanas, negocioSemanas, dias]) => {
                if (vivo) {
                    setDeUno({
                        id,
                        datos: {
                            vigente: semanas.data.find((s) => s.status === 'current') ?? null,
                            semanaNegocio: negocioSemanas?.data.find((s) => s.status === 'current') ?? null,
                            dias: dias.data.filter((d) => d.status === 'active').sort((a, b) => a.start_date.localeCompare(b.start_date)),
                        },
                    });
                    setErrorUno(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setErrorUno(e);
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio, calId, hoy, intento]);

    // Las citas afectadas por resolver, con el nombre de cada cliente.
    useEffect(() => {
        if (!puede(PERMISOS.recolocar)) {
            return;
        }
        let vivo = true;
        Promise.all([ops.listAppointmentRelocations(negocio, { status: 'pending', limit: 100 }), ops.listAppointmentRelocations(negocio, { status: 'needs_customer', limit: 100 })])
            .then(async ([a, b]) => {
                const lista = [...a.data, ...b.data].sort((x, y) => (x.appointment?.starts_at ?? '').localeCompare(y.appointment?.starts_at ?? ''));
                const ids = [...new Set(lista.map((r) => r.appointment?.customer_id).filter((x): x is string => typeof x === 'string'))];
                const conNombre: Record<string, string> = {};
                if (ids.length > 0) {
                    const clientes = await ops.listCustomersByIds(negocio, ids).catch(() => null);
                    for (const cl of clientes?.data ?? []) {
                        conNombre[cl.id] = cl.display_name;
                    }
                }
                if (vivo) {
                    setRelocs(lista);
                    setNombres(conNombre);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio, intento]);

    // Los ajustes de la agenda que tocan a los horarios (G86).
    useEffect(() => {
        if (!puede(PERMISOS.ajustes)) {
            return;
        }
        let vivo = true;
        ops.getAppointmentSettingsConVersion(negocio)
            .then((r) => {
                if (vivo) {
                    setAjustes(r);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio]);

    const sel = elegido?.id ?? '';
    const borrador = borradores[sel];
    const aplicaDesde = desde[sel] ?? hoy;
    const d = deUno?.id === sel ? deUno.datos : null;
    const semanaNegocio = semanaDeTramos(d?.semanaNegocio?.intervals ?? []);
    const heredaGuardada = d?.vigente == null || d.vigente.inherits;
    const semanaGuardada = heredaGuardada ? semanaNegocio : semanaDeTramos(d.vigente?.intervals ?? []);
    const hereda = borrador?.hereda ?? heredaGuardada;
    const bloques = borrador !== undefined && !borrador.hereda ? borrador.bloques : bloquesDeSemana(hereda ? semanaNegocio : semanaGuardada);
    const errores = erroresDeBloques(bloques);
    const cambiado = borrador !== undefined;
    const nombre = elegido?.display_name ?? '';

    const guardar = () => {
        if (elegido === null || borrador === undefined || (!borrador.hereda && hayErrores(errores))) {
            return;
        }
        const cal = elegido.calendar_id;
        const clave = elegido.id;
        const fecha = aplicaDesde;
        const semana: NuevaSemana = borrador.hereda ? { valid_from: fecha, inherits: true } : { valid_from: fecha, inherits: false, intervals: tramosDeSemana(semanaDeBloques(borrador.bloques)) };
        const guardado = borrador;
        const cambio: Cambio = {
            contexto: `Semana tipo de ${nombre} · desde el ${fechaLarga(fecha, anio)}`,
            icono: 'clock',
            todoElNegocio: false,
            peticion: { kind: 'weekly_schedule', calendar_id: cal, weekly_schedule: semana },
            aplicar: (changeId, k) =>
                ops.createWeeklySchedule(negocio, cal, { ...semana, ...(changeId === undefined ? {} : { change_id: changeId }) }, k).then((r) => {
                    if (r.warnings.length > 0) {
                        avisar({ tipo: 'ok', texto: `${primerNombre(nombre)} trabaja fuera del horario del negocio; se guarda igual.` });
                    }
                }),
            textoDeshacer: `Horario de ${primerNombre(nombre)} guardado. Se aplica desde ${fecha === hoy ? 'hoy' : `el ${fechaLarga(fecha, anio)}`}.`,
            alProgramar: () => {
                setBorradores((b) => sin(b, clave));
            },
            alDeshacer: () => {
                setBorradores((b) => ({ ...b, [clave]: guardado }));
            },
            alGuardar: recargar,
        };
        void c.pedir(cambio);
    };

    useCabecera({
        ayuda: AYUDA.horarios,
        acciones:
            !editable || elegido === null
                ? []
                : cambiado
                  ? [
                        { clave: 'descartar', texto: 'Descartar', icono: 'x' as const, secundaria: true, onClick: () => { setBorradores((b) => sin(b, sel)); } },
                        { clave: 'guardar', texto: c.calculando ? 'Calculando…' : 'Guardar horario', icono: c.calculando ? ('loader-circle' as const) : ('check' as const), onClick: guardar },
                    ]
                  : [{ clave: 'copiar', texto: movil ? 'Copiar a otros' : 'Copiar a otros profesionales', icono: 'copy' as const, secundaria: true, onClick: () => { setCopiar({ a: [], desde: hoy }); } }],
    });

    if (error !== null) {
        return <EstadoError seccion="los horarios del equipo" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={reintentar} movil={movil} />;
    }
    if (datos === null) {
        return <EstadoCargando seccion="los horarios del equipo" />;
    }

    const porResolver = relocs.filter((r) => r.status === 'pending' || r.status === 'needs_customer');
    const nombreDe = (id: string | null | undefined) => datos.profesionales.find((p) => p.id === id)?.display_name ?? '';

    const cambiarAjuste = (cambios: Partial<AjustesAgenda>, texto: string) => {
        if (ajustes?.version == null) {
            return;
        }
        ops.updateAppointmentSettings(negocio, cambios, ajustes.version)
            .then((r) => {
                setAjustes(r);
                avisar({ tipo: 'ok', texto });
            })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: e instanceof ErrorApi && e.estado === 412 ? 'Alguien lo ha cambiado mientras tanto. Vuelve a abrir la pantalla.' : textoDeError(e) }); });
    };

    return (
        <div className="rv-hor rv-sv-hor">
            {c.envio.pendiente !== null && <Deshacer texto={c.envio.pendiente.texto} onDeshacer={c.envio.deshacer} clave={c.envio.pendiente.clave} duracion={c.envio.duracion} movil={movil} />}
            {c.hecho !== null && (
                <div role="status" className="rv-ca-hecho">
                    <span className="rv-ca-hecho__baldosa">
                        <Icono nombre="check" />
                    </span>
                    <span>{c.hecho}</span>
                </div>
            )}
            {soloLectura && (
                <p className="rv-sv-nota">
                    <Icono nombre="eye" />
                    <span>{SOLO_LECTURA}</span>
                </p>
            )}

            {porResolver.length > 0 && (
                <section className="rv-sv-tarjeta" aria-label="Citas afectadas">
                    <div className="rv-sv-tarjeta__cab">
                        <span className="rv-sv-baldosa" style={{ background: 'var(--rv-warning)', color: 'var(--rv-ink)' }}>
                            <Icono nombre="calendar-clock" />
                        </span>
                        <span className="rv-sv-celda__texto">
                            <strong>{porResolver.length === 1 ? '1 cita afectada por resolver' : `${String(porResolver.length)} citas afectadas por resolver`}</strong>
                            <small>Siguen guardadas en su hora hasta que decidas. Al resolverlas se avisa al cliente y al profesional.</small>
                        </span>
                    </div>
                    <div role="list" aria-label="Citas afectadas">
                        {porResolver.map((r) => {
                            const a = r.appointment;
                            const b = baldosa(a?.local_date ?? hoy);
                            const seg = a?.segments[0];
                            const pro = seg?.professional_display_name ?? nombreDe(seg?.professional_id);
                            const cliente = a?.customer_id == null ? 'Cliente' : (nombres[a.customer_id] ?? 'Cliente');
                            const eligio = a?.professional_choice === 'any' ? 'pidió «cualquiera»' : `eligió a ${primerNombre(pro)}`;
                            const libre = r.proposed_professional_id == null ? '' : ` · ${primerNombre(nombreDe(r.proposed_professional_id))} está libre a esa hora`;
                            const est = ESTADO_RECOLOCACION[r.status];
                            return (
                                <div key={r.id} role="listitem" className="rv-sv-fila-dia">
                                    <span className="rv-sv-dia">
                                        <strong>{b.dia}</strong>
                                        <small>{b.mes}</small>
                                    </span>
                                    <span className="rv-sv-celda__texto" style={{ flex: 1 }}>
                                        <strong>{`${a?.local_time ?? ''} · ${cliente}`}</strong>
                                        <small>{`${seg?.service_name ?? ''} con ${pro} · ${eligio} · ${POR_QUE[r.cause]}${libre}`}</small>
                                    </span>
                                    {resuelve ? (
                                        <Boton tipo="secundario" icono="arrow-right" onClick={() => { setResolver(r); }}>
                                            Resolver
                                        </Boton>
                                    ) : (
                                        <Chip estado={est.chip}>{est.texto}</Chip>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {elegido === null ? (
                <div className="rv-sv-vacia">
                    <strong>No hay profesionales activos</strong>
                    <span>Activa a alguien en Profesionales para ver y cambiar su horario.</span>
                </div>
            ) : (
                <>
                    <Contexto etiqueta="Horario de" valor={sel} opciones={activos.map((p) => ({ valor: p.id, texto: borradores[p.id] === undefined ? p.display_name : `${p.display_name} · cambios sin guardar` }))} icono="user" movil={movil} onChange={(v) => { navegar(`${ruta}?apartado=horarios&profesional=${encodeURIComponent(v)}`, true); }} />
                    {errorUno !== null ? (
                        <EstadoError seccion={`el horario de ${nombre}`} codigoSoporte={estadoDeError(errorUno).codigoSoporte} onReintentar={recargar} movil={movil} />
                    ) : d === null ? (
                        <EstadoCargando seccion={`el horario de ${nombre}`} />
                    ) : (
                        <>
                            <section className="rv-sv-tarjeta" aria-label={`Semana tipo de ${nombre}`}>
                                <Interruptor
                                    etiqueta="Horario propio"
                                    ayuda={hereda ? 'Usa el horario del negocio, que se cambia en Horario.' : 'Tiene su propia semana. Apagado, vuelve a usar el horario del negocio.'}
                                    valor={!hereda}
                                    desactivado={!editable}
                                    linea="ninguna"
                                    onChange={(v) => {
                                        setBorradores((b) => {
                                            const vuelve = v === !heredaGuardada;
                                            return vuelve ? sin(b, sel) : { ...b, [sel]: { hereda: !v, bloques: bloquesDeSemana(semanaNegocio) } };
                                        });
                                    }}
                                />
                                {hereda && (
                                    <div>
                                        <Boton tipo="secundario" icono="clock" onClick={() => { navegar('/negocio?apartado=horario'); }}>
                                            Ver el horario del negocio
                                        </Boton>
                                    </div>
                                )}
                            </section>
                            {cambiado && (
                                <section className="rv-sv-tarjeta rv-sv-tarjeta--borrador">
                                    <div className="rv-sv-tarjeta__cab">
                                        <span className="rv-sv-baldosa" style={{ background: 'var(--rv-paper)', color: 'var(--rv-ink)' }}>
                                            <Icono nombre="pencil" />
                                        </span>
                                        <span className="rv-sv-celda__texto">
                                            <strong>Cambios sin guardar</strong>
                                            <small>{borrador.hereda !== heredaGuardada ? (borrador.hereda ? 'Volverá a usar el horario del negocio.' : 'Tendrá su propia semana, que empieza como la del negocio.') : 'Revisa los días que has cambiado y guarda el horario.'}</small>
                                        </span>
                                    </div>
                                    <Fecha etiqueta="Se aplica desde" valor={aplicaDesde} min={hoy} ayuda="Las citas ya hechas no cambian solas." onChange={(v) => { setDesde((x) => ({ ...x, [sel]: v })); }} />
                                </section>
                            )}
                            <Semana
                                bloques={bloques}
                                errores={errores}
                                editable={editable && !hereda}
                                atenuado={hereda}
                                porDefecto={tramoPorDefecto(true)}
                                movil={movil}
                                onCambiar={(b) => { setBorradores((x) => ({ ...x, [sel]: { hereda: false, bloques: b } })); }}
                            />
                            <p className="rv-sv-nota">
                                <Icono nombre="history" />
                                <span>
                                    {heredaGuardada
                                        ? `Usa el horario del negocio${d.vigente === null ? '' : ` desde el ${fechaLarga(d.vigente.valid_from, anio)}`}.`
                                        : `Vigente desde el ${fechaLarga(d.vigente?.valid_from ?? hoy, anio)}. Las versiones anteriores se guardan: una semana tipo nunca se sobrescribe.`}
                                </span>
                            </p>

                            <section className="rv-sv-tarjeta" aria-label="Días especiales">
                                <div className="rv-sv-tarjeta__cab">
                                    <span className="rv-sv-celda__texto" style={{ flex: 1 }}>
                                        <strong>{`Días especiales · ${String(d.dias.length)}`}</strong>
                                        <small>Vacaciones, médico, formación… Mandan sobre la semana tipo.</small>
                                    </span>
                                    {editable && (
                                        <Boton tipo="secundario" icono="plus" onClick={() => { setDia({ dia: null }); }}>
                                            Añadir día especial
                                        </Boton>
                                    )}
                                </div>
                                {d.dias.length === 0 ? (
                                    <div className="rv-sv-vacia" style={{ boxShadow: 'none', padding: '24px 16px' }}>
                                        <strong>{`${primerNombre(nombre)} no tiene días especiales`}</strong>
                                        <span>Vacaciones, una cita médica o una formación: el día que no trabaja o trabaja con otro horario.</span>
                                    </div>
                                ) : (
                                    <div role="list" aria-label="Días especiales">
                                        {d.dias.map((x) => {
                                            const b = baldosa(x.start_date);
                                            const l = lineaDiaEspecial(x, anio);
                                            return (
                                                <button key={x.id} type="button" role="listitem" className="rv-sv-fila-dia rv-sv-fila-dia--boton" onClick={() => { setDia({ dia: x }); }}>
                                                    <span className={`rv-sv-dia${x.kind === 'closed' ? ' rv-sv-dia--cerrado' : ''}`}>
                                                        <strong>{b.dia}</strong>
                                                        <small>{b.mes}</small>
                                                    </span>
                                                    <span className="rv-sv-celda__texto" style={{ flex: 1 }}>
                                                        <strong>{l.titulo}</strong>
                                                        <small>{l.sub}</small>
                                                    </span>
                                                    <Chip estado="neutral">{l.chip}</Chip>
                                                    <Icono nombre="chevron-right" />
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                <p className="rv-sv-nota">
                                    <Icono nombre="info" />
                                    <span>Los festivos y cierres de todo el negocio también le cierran. Se cambian en Horario.</span>
                                </p>
                            </section>
                        </>
                    )}
                </>
            )}

            {ajustes !== null && elegido !== null && (
                <section className="rv-sv-tarjeta" aria-label="Los profesionales y su horario">
                    <Interruptor
                        etiqueta="Los profesionales pueden cambiar su horario"
                        ayuda={ajustes.datos.professional_can_edit_schedule ? 'Desde Mi agenda, los que tienen acceso al panel.' : 'Apagado, solo lo cambiáis desde aquí.'}
                        valor={ajustes.datos.professional_can_edit_schedule}
                        desactivado={soloLectura}
                        linea={ajustes.datos.professional_can_edit_schedule ? 'abajo' : 'ninguna'}
                        onChange={(v) => { cambiarAjuste({ professional_can_edit_schedule: v }, v ? 'Los profesionales pueden cambiar su horario desde Mi agenda.' : 'Los profesionales ya no pueden cambiar su horario.'); }}
                    />
                    {ajustes.datos.professional_can_edit_schedule && (
                        <div role="radiogroup" aria-label="Si su cambio deja citas fuera" className="rv-sv-grupo">
                            <span className="rv-sv-grupo__t">Si su cambio deja citas fuera</span>
                            <OpcionRadio elegida={ajustes.datos.professional_schedule_change_mode === 'request'} bloqueada={soloLectura} titulo="Lo confirmáis vosotros" texto="Queda como solicitud hasta que la apruebe un encargado o un administrador." onElegir={() => { if (ajustes.datos.professional_schedule_change_mode !== 'request') { cambiarAjuste({ professional_schedule_change_mode: 'request' }, 'Ajuste guardado.'); } }} />
                            <OpcionRadio elegida={ajustes.datos.professional_schedule_change_mode === 'apply'} bloqueada={soloLectura} titulo="Se aplica al momento" texto="Las citas que deja fuera pasan a «Citas afectadas»." onElegir={() => { if (ajustes.datos.professional_schedule_change_mode !== 'apply') { cambiarAjuste({ professional_schedule_change_mode: 'apply' }, 'Ajuste guardado.'); } }} />
                        </div>
                    )}
                </section>
            )}

            <Impacto impacto={c.impacto} palabras={PALABRAS} anio={anio} movil={movil} puedeConfirmar={puedeConfirmar} onConfirmar={() => { void c.confirmar(); }} onCerrar={c.cerrarImpacto} />

            {elegido !== null && d !== null && (
                <DiaEspecialPanel
                    ops={ops}
                    negocio={negocio}
                    movil={movil}
                    abierto={dia !== null}
                    dia={dia?.dia ?? null}
                    profesional={elegido}
                    hoy={hoy}
                    anio={anio}
                    editable={editable}
                    calculando={c.calculando}
                    pedir={c.pedir}
                    onCerrar={() => { setDia(null); }}
                    onGuardado={recargar}
                />
            )}

            {resolver !== null && <Resolver {...props} recolocacion={resolver} cliente={resolver.appointment?.customer_id == null ? 'Cliente' : (nombres[resolver.appointment.customer_id] ?? 'Cliente')} profesionales={datos.profesionales} onCerrar={() => { setResolver(null); }} onHecho={() => { setResolver(null); recargar(); }} />}

            <Ventana
                abierta={copiar !== null && elegido !== null}
                titulo={`Copiar la semana de ${primerNombre(nombre)}`}
                texto="Pasan a tener esta misma semana tipo. Sus días especiales no se tocan."
                accion="Copiar"
                accionIcono="copy"
                inactiva={copiar === null || copiar.a.length === 0}
                movil={movil}
                onAccion={() => {
                    if (copiar === null || elegido === null) {
                        return;
                    }
                    const destinos = activos.filter((p) => copiar.a.includes(p.id));
                    const datosCopia = { target_calendar_ids: destinos.map((p) => p.calendar_id), valid_from: copiar.desde };
                    const origen = elegido.calendar_id;
                    setCopiar(null);
                    void c.pedir({
                        contexto: `Copiar la semana de ${nombre} a ${unir(destinos.map((p) => p.display_name))}`,
                        icono: 'copy',
                        todoElNegocio: false,
                        peticion: { kind: 'copy', calendar_id: origen, copy: datosCopia },
                        aplicar: (changeId, k) => ops.copyWeeklySchedule(negocio, origen, { ...datosCopia, ...(changeId === undefined ? {} : { change_id: changeId }) }, k).then(() => undefined),
                        textoDeshacer: `Semana copiada a ${unir(destinos.map((p) => p.display_name))} desde el ${fechaLarga(datosCopia.valid_from, anio)}.`,
                        alGuardar: recargar,
                    });
                }}
                onCerrar={() => { setCopiar(null); }}
            >
                {copiar !== null && (
                    <>
                        {activos
                            .filter((p) => p.id !== sel)
                            .map((p) => (
                                <Interruptor key={p.id} etiqueta={p.display_name} valor={copiar.a.includes(p.id)} compacto onChange={(v) => { setCopiar((x) => (x === null ? x : { ...x, a: v ? [...x.a, p.id] : x.a.filter((y) => y !== p.id) })); }} />
                            ))}
                        <Fecha etiqueta="Desde" valor={copiar.desde} min={hoy} onChange={(v) => { setCopiar((x) => (x === null ? x : { ...x, desde: v })); }} />
                    </>
                )}
            </Ventana>
        </div>
    );
}

function sin<T>(o: Readonly<Record<string, T>>, clave: string): Record<string, T> {
    return Object.fromEntries(Object.entries(o).filter(([k]) => k !== clave));
}
